/**
 * Storecontext : fetch HTTP + état UI (gate prix).
 * Writers HTTP : script vanilla listing (pré-bundle) + ensureStoreContextFetch (ModelProvider / StoreModel).
 */

import type CartModel from "@src/backbone/model/CartModel";
import type StoreModel from "@src/backbone/model/StoreModel";
import { GATSBY_SOURCE_SUSHISHOP_BASE_URL } from "@src/utils/constants";

export type StoreContextProductModifier = {
  id_product: string;
  available?: boolean;
  price?: number;
  strikethrough_price?: number;
  price_ttc?: string;
  price_ttc_vae?: string;
};

type StoreContextPrefetchPayload = {
  id_store: string;
  mode: "vae" | "vad";
  date_url: string;
  products: StoreContextProductModifier[];
  expire_date?: string;
  payment_modes?: unknown[];
  [key: string]: unknown;
};

type StoreContextPrefetchHandle = {
  id_store: string;
  mode: "vae" | "vad";
  date_url: string;
  /** Segmentation client (statut MySushiShop) du contexte demandé. 0 = anonyme/non segmenté (défaut du loader early). */
  segmentation: number;
  promise: Promise<StoreContextPrefetchPayload>;
  /** Résolu une fois (évite double parse). */
  data?: StoreContextPrefetchPayload;
  /** Promise déjà attachée à consumeWindowPrefetch. */
  __consumed?: boolean;
};

type EnsureStoreContextFetchOptions = {
  id_store?: string | null;
  mode?: "vae" | "vad";
  /** Défaut "now". Autre date → pas de cache window (StoreModel.fetch Backbone). */
  date_url?: string;
  /** Segmentation client (statut MySushiShop) connue côté appelant (StoreModel via CustomerModel). Défaut 0. */
  segmentation?: number;
};

declare global {
  interface Window {
    __RS_STORECONTEXT_PREFETCH__?: StoreContextPrefetchHandle;
  }
}

// --- État UI (gate prix) ---

let storeContextVersion = 0;
const storeContextListeners = new Set<() => void>();
let boundCart: CartModel | null = null;
/** id_store pour lequel le dernier sync storecontext a réussi. */
let syncedStoreContextId = "";
/**
 * Clé complète du contexte en cache : `id_store|mode|date_url`.
 * La clé Backbone est ce triplet, pas le seul id_store : sans lui, des prix VAE
 * peuvent rester affichés en VAD (ou ceux d'une commande programmée en "now").
 * Cache mono-entrée : il n'est peuplé que pour le contexte courant, invalidé dès
 * que le panier change de mode ou de créneau (cf. bindCartStoreContext).
 */
let syncedStoreContextKey = "";
/** Instance Store qui détient le contexte synchronisé (évite mismatch getStore). */
let syncedStoreRef: StoreModel | null = null;
/** Prix produits du prefetch / sync — utilisables avant StoreModel Backbone. */
const syncedProductsById: Record<string, StoreContextProductModifier> = {};
let windowPrefetchListenerBound = false;

const bumpStoreContextVersion = () => {
  storeContextVersion += 1;
  storeContextListeners.forEach((listener) => listener());
};

const indexProducts = (products?: StoreContextProductModifier[]) => {
  Object.keys(syncedProductsById).forEach((key) => {
    delete syncedProductsById[key];
  });
  (products || []).forEach((product) => {
    if (product?.id_product != null) {
      syncedProductsById[String(product.id_product)] = product;
    }
  });
};

/** Attributs panier qui changent le triplet id_store/mode/date_url du storecontext. */
const CART_CONTEXT_EVENTS = [
  "change:id_store",
  "change:vae",
  "change:now",
  "change:order_date",
  "change:order_time",
] as const;

const buildContextKey = (
  idStore: string | number | null | undefined,
  mode?: string | null,
  dateUrl?: string | null
) => `${String(idStore || "")}|${mode || "vad"}|${dateUrl || "now"}`;

/** Mode + créneau visés par le panier (aligné sur StoreModel.fetchStoreContext). */
const resolveCartContextKey = (): string | null => {
  if (!boundCart) return null;
  const idStore = boundCart.get("id_store");
  if (!idStore || String(idStore) === "0") return null;
  const mode = boundCart.isVAE() ? "vae" : "vad";
  const dateUrl = boundCart.isNow()
    ? "now"
    : boundCart
        .getDatetime()
        .toISOString()
        .replace(/^(.{10})T((..):(..)).*/, "$1-$3-$4");
  return buildContextKey(idStore, mode, dateUrl);
};

/**
 * Segmentation MySushiShop attendue, lue en localStorage (CustomerModel).
 * Sert de garde : un prefetch anonyme ne doit pas alimenter les prix d'un client segmenté.
 * Une valeur périmée ne peut que rendre la garde plus stricte (prix affichés plus tard).
 */
const resolveExpectedSegmentation = (): number => {
  if (typeof window === "undefined") return 0;
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (!key || !/CustomerModel/i.test(key) || /_expires$/i.test(key)) continue;
    const raw = localStorage.getItem(key);
    if (!raw) continue;
    try {
      const parsed = JSON.parse(raw) as {
        comein?: boolean;
        segmentation?: string | number;
      };
      if (parsed?.comein !== true) continue;
      return Number(parsed.segmentation) || 0;
    } catch {
      continue;
    }
  }
  return 0;
};

/** Vide le cache prix : le contexte visé n'est plus celui qui est en cache. */
const invalidateStoreContext = () => {
  if (!syncedStoreContextId && !syncedStoreContextKey) return;
  syncedStoreContextId = "";
  syncedStoreContextKey = "";
  syncedStoreRef = null;
  indexProducts([]);
  bumpStoreContextVersion();
};

/** Invalide le cache si le panier ne vise plus le contexte mis en cache. */
const invalidateStoreContextIfStale = () => {
  const cartKey = resolveCartContextKey();
  if (!cartKey || !syncedStoreContextKey) return;
  if (cartKey !== syncedStoreContextKey) invalidateStoreContext();
};

/** Prefetch résolu (ou sync) — débloque le gate sans attendre cart.getStore(). */
const markStoreContextProducts = (
  idStore: string,
  products: StoreContextProductModifier[],
  mode?: string | null,
  dateUrl?: string | null
) => {
  syncedStoreContextId = String(idStore || "");
  syncedStoreContextKey = buildContextKey(idStore, mode, dateUrl);
  indexProducts(products);
  bumpStoreContextVersion();
};

export const isStoreContextSynced = (idStore?: string | null) =>
  !!idStore &&
  String(idStore) === syncedStoreContextId &&
  Object.keys(syncedProductsById).length > 0;

/**
 * Consomme le handle window (vanilla / ensure) → markProducts.
 * Idempotent : une seule attache promise, un seul marquage par store.
 */
const consumeWindowPrefetch = (): boolean => {
  if (typeof window === "undefined") return false;
  const pref = window.__RS_STORECONTEXT_PREFETCH__;
  if (!pref?.id_store) return false;
  const prefKey = buildContextKey(pref.id_store, pref.mode, pref.date_url);
  // Déjà marqué : sortie avant toute vérification (appelé à chaque rendu de carte).
  if (
    syncedStoreContextKey === prefKey &&
    Object.keys(syncedProductsById).length > 0
  ) {
    return true;
  }
  // Le prefetch early devine le mode depuis l'URL/localStorage : dès qu'un panier
  // est monté, il fait foi — un prefetch d'un autre contexte ne doit pas peupler le cache.
  const cartKey = resolveCartContextKey();
  if (cartKey && cartKey !== prefKey) return false;
  if (pref.data?.products?.length) {
    // Prefetch anonyme (segmentation 0) : ses prix ne valent pas pour un client avec un
    // statut MySushiShop (offres PriceSpecific par segment) — RUN-8108.
    if (Number(pref.segmentation || 0) !== resolveExpectedSegmentation()) {
      return false;
    }
    markStoreContextProducts(
      pref.id_store,
      pref.data.products,
      pref.mode,
      pref.date_url
    );
    return true;
  }
  if (pref.promise && !pref.__consumed) {
    pref.__consumed = true;
    void pref.promise
      .then((data) => {
        if (pref && !pref.data) pref.data = data;
        consumeWindowPrefetch();
      })
      .catch(() => {});
  }
  return false;
};

/** Hydrate depuis le prefetch inline du loader web-performance dès que data est là. */
export const hydrateStoreContextFromWindowPrefetch = (): boolean =>
  consumeWindowPrefetch();

/** Appelé depuis StoreModel.onSync — ne dépend pas de cart.getStore(). */
export const markStoreContextSynced = (idStore: string, store: StoreModel) => {
  syncedStoreContextId = String(idStore || "");
  syncedStoreRef = store;
  const ctx = (
    store as StoreModel & {
      _storeContextModel?: { get: (k: string) => unknown };
    }
  )._storeContextModel;
  syncedStoreContextKey = buildContextKey(
    idStore,
    ctx?.get?.("mode") as string | undefined,
    ctx?.get?.("date_url") as string | undefined
  );
  const ctxProducts = ctx?.get?.("products") as
    | StoreContextProductModifier[]
    | undefined;
  if (Array.isArray(ctxProducts) && ctxProducts.length) {
    indexProducts(ctxProducts);
  }
  bumpStoreContextVersion();
};

/** Store avec contexte prêt pour cet id (fallback si cart.getStore() mismatch). */
export const getSyncedStoreContext = (
  idStore?: string | null
): StoreModel | null => {
  if (!idStore || String(idStore) !== syncedStoreContextId) return null;
  return syncedStoreRef;
};

/** Modificateur produit du contexte sync/prefetch. */
export const getSyncedProductModifier = (
  idStore?: string | null,
  idProduct?: string | null
): StoreContextProductModifier | null => {
  if (!idStore || !idProduct) return null;
  if (String(idStore) !== syncedStoreContextId) return null;
  return syncedProductsById[String(idProduct)] || null;
};

export const bindCartStoreContext = (cart: CartModel) => {
  if (typeof window !== "undefined" && !windowPrefetchListenerBound) {
    windowPrefetchListenerBound = true;
    window.addEventListener("rs-storecontext-prefetch", () => {
      consumeWindowPrefetch();
    });
    consumeWindowPrefetch();
  }
  if (boundCart === cart) return;
  if (boundCart) {
    boundCart.off("changeContext", bumpStoreContextVersion);
    boundCart.off("change:id_store", bumpStoreContextVersion);
    CART_CONTEXT_EVENTS.forEach((event) => {
      boundCart?.off(event, invalidateStoreContextIfStale);
    });
  }
  boundCart = cart;
  cart.on("changeContext", bumpStoreContextVersion);
  cart.on("change:id_store", bumpStoreContextVersion);
  // Mode (VAE/VAD) ou créneau modifiés → le cache prix ne vise plus le bon contexte.
  CART_CONTEXT_EVENTS.forEach((event) => {
    cart.on(event, invalidateStoreContextIfStale);
  });
  invalidateStoreContextIfStale();
};

export const subscribeStoreContext = (onStoreChange: () => void) => {
  storeContextListeners.add(onStoreChange);
  return () => {
    storeContextListeners.delete(onStoreChange);
  };
};

export const getStoreContextVersion = () => storeContextVersion;
export const getStoreContextVersionServer = () => 0;

// --- Fetch HTTP (cache window) ---

const resolveLocale = (): string => {
  if (typeof window === "undefined") return "fr";
  const seg = window.location.pathname.split("/").filter(Boolean)[0];
  return seg && seg.length === 2 ? seg : "fr";
};

/** Lit id_store (+ mode) depuis ?id_store= ou localStorage CartModel. */
const resolveStoreContextTarget = (): {
  id_store: string;
  mode: "vae" | "vad";
} | null => {
  if (typeof window === "undefined") return null;
  try {
    const fromUrl = new URLSearchParams(window.location.search).get("id_store");
    if (fromUrl && fromUrl !== "0") {
      // Deep-link listing → VAE (aligné useInitSelectedStore).
      return { id_store: String(fromUrl), mode: "vae" };
    }
  } catch {
    /* ignore */
  }
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    // HYDRATION-FIX: exclut `CartModel_XX_expires` (valeur `Date.toString()`, pas du JSON)
    // — le regex seul matchait aussi cette clé, causant un JSON.parse invalide.
    if (!key || !/CartModel/i.test(key) || /_expires$/i.test(key)) continue;
    const raw = localStorage.getItem(key);
    if (!raw) continue;
    try {
      const parsed = JSON.parse(raw) as {
        id_store?: string | number;
        vae?: boolean | number | string;
      };
      const id = parsed?.id_store != null ? String(parsed.id_store) : "";
      if (!id || id === "0") continue;
      const vae = parsed.vae === true || parsed.vae === 1 || parsed.vae === "1";
      return { id_store: id, mode: vae ? "vae" : "vad" };
    } catch {
      // clé au format inattendu — on passe à la suivante plutôt que d'abandonner le scan.
      continue;
    }
  }
  return null;
};

const isValidStoreContextPayload = (
  data: unknown
): data is StoreContextPrefetchPayload => {
  if (!data || typeof data !== "object") return false;
  const payload = data as StoreContextPrefetchPayload & {
    codeFrame?: unknown;
  };
  // Réponse Gatsby develop (:8080/api sans proxy) — pas un storecontext API.
  if (payload.codeFrame != null) return false;
  if (!Array.isArray(payload.products) || payload.products.length === 0) {
    return false;
  }
  // Checkout a besoin de payment_modes ; sans tableau → laisser Backbone refetch.
  if (!Array.isArray(payload.payment_modes)) return false;
  return true;
};

const buildStoreContextUrl = (
  idStore: string,
  mode: "vae" | "vad",
  dateUrl = "now",
  segmentation = 0
): string => {
  const locale = resolveLocale();
  // Absolute URL: relative `/api/...` hits Gatsby on :8080 (no proxy) and
  // returns SSR error JSON instead of payment_modes / products.
  const base = (GATSBY_SOURCE_SUSHISHOP_BASE_URL || "").replace(/\/$/, "");
  // Aligné sur StoreContextModel.url() (segment segmentation en fin de path).
  const path = `/api/${locale}/storecontext/${idStore}/${mode}/${dateUrl}/${segmentation}?sync=initial`;
  return base ? `${base}${path}` : path;
};

/**
 * Démarre ou réutilise le fetch /storecontext (coalesce sur window cache).
 * date_url !== "now" → no-op (laisse StoreModel.fetch Backbone).
 */
export const ensureStoreContextFetch = (
  options?: EnsureStoreContextFetchOptions
): StoreContextPrefetchHandle | null => {
  if (typeof window === "undefined") return null;

  const date_url = options?.date_url || "now";
  // Cache window réservé au sync initial (listing / prix ASAP).
  if (date_url !== "now") return null;

  const resolved = resolveStoreContextTarget();
  const target = options?.id_store
    ? {
        id_store: String(options.id_store),
        mode: options.mode || resolved?.mode || ("vad" as const),
      }
    : resolved;
  if (!target?.id_store || target.id_store === "0") return null;
  const segmentation = Number(options?.segmentation || 0);

  const existing = window.__RS_STORECONTEXT_PREFETCH__;
  if (
    existing &&
    existing.id_store === target.id_store &&
    existing.mode === target.mode &&
    existing.date_url === date_url &&
    Number(existing.segmentation || 0) === segmentation
  ) {
    // Ne pas coalescer un prefetch invalide (JSON Gatsby codeFrame sur :8080).
    if (!existing.data || isValidStoreContextPayload(existing.data)) {
      consumeWindowPrefetch();
      return existing;
    }
    // data invalide → relance un fetch absolu ci-dessous
  }

  const url = buildStoreContextUrl(
    target.id_store,
    target.mode,
    date_url,
    segmentation
  );
  // credentials: include — URL absolue cross-origin (:8080 → https backend)
  const requestInit: RequestInit & { priority: "low" } = {
    credentials: "include",
    priority: "low",
  };
  const promise = fetch(url, requestInit)
    .then((res) => {
      if (!res.ok) throw new Error(`storecontext ${res.status}`);
      return res.json() as Promise<StoreContextPrefetchPayload>;
    })
    .then((data) => {
      const handle = window.__RS_STORECONTEXT_PREFETCH__;
      if (handle && handle.id_store === target.id_store) {
        if (!isValidStoreContextPayload(data)) {
          throw new Error("storecontext invalid payload");
        }
        handle.data = data;
      }
      return data;
    });

  const handle: StoreContextPrefetchHandle = {
    id_store: target.id_store,
    mode: target.mode,
    date_url,
    segmentation,
    promise,
  };
  window.__RS_STORECONTEXT_PREFETCH__ = handle;
  consumeWindowPrefetch();
  return handle;
};

/** Attache le prefetch résolu à un StoreContextModel Backbone (évite 2e réseau). */
export const applyPrefetchToStoreContextModel = async (
  storeContextModel: {
    id?: string | number;
    get: (key: string) => unknown;
    set: (attrs: Record<string, unknown>, opts?: { silent?: boolean }) => void;
    trigger: (event: string) => void;
  }
): Promise<boolean> => {
  if (typeof window === "undefined") return false;
  const pref = window.__RS_STORECONTEXT_PREFETCH__;
  if (!pref) return false;
  const id = String(
    storeContextModel.id || storeContextModel.get("id_store") || ""
  );
  if (pref.id_store !== id) return false;
  if (pref.mode !== storeContextModel.get("mode")) return false;
  if (pref.date_url !== (storeContextModel.get("date_url") || "now")) {
    return false;
  }
  if (
    Number(pref.segmentation || 0) !==
    Number(storeContextModel.get("segmentation") || 0)
  ) {
    return false;
  }

  try {
    const data = pref.data || (await pref.promise);
    if (!isValidStoreContextPayload(data)) {
      return false;
    }
    storeContextModel.set(
      {
        ...data,
        id_store: id,
        products: data.products,
      },
      { silent: true }
    );
    storeContextModel.trigger("sync");
    return true;
  } catch {
    return false;
  }
};
